import { EventEmitter } from 'utils/events';
import log from 'utils/log';

interface SpeakerDetectionOptions {
  threshold: number;
  interval: number;
  speakingTimeout: number;
}

export class SpeakerDetector extends EventEmitter {
  private options: SpeakerDetectionOptions;
  private audioContext: AudioContext;
  private audioAnalysers: Map<string, AnalyserNode> = new Map();
  private lastSpeakTime: Map<string, number> = new Map();
  private intervalId: number | null = null;
  private currentActiveSpeaker: string | null = null;

  constructor(options: Partial<SpeakerDetectionOptions> = {}) {
    super();
    this.options = {
      threshold: 0.05,
      interval: 100,
      speakingTimeout: 1500,
      ...options,
    };

    this.audioContext = new AudioContext();
  }

  public async start(): Promise<void> {
    if (this.intervalId !== null) return;

    this.intervalId = window.setInterval(() => this.detectSpeaker(), this.options.interval);
    log.log('[SPEAKER DETECTOR] Started speaker detection', Date.now().toString().slice(-5));
  }

  public stop(): void {
    if (this.intervalId === null) return;

    window.clearInterval(this.intervalId);
    this.intervalId = null;
    log.log('[SPEAKER DETECTOR] Stopped speaker detection', Date.now().toString().slice(-5));

    if (this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }

    // Recreate AudioContext for future use
    this.audioContext = new AudioContext();
    this.audioAnalysers.clear();
    this.lastSpeakTime.clear();
  }

  public addStream(connectionId: string, stream: MediaStream): void {
    if (!stream) {
      log.log('[SPEAKER DETECTOR] No stream provided for', connectionId, Date.now().toString().slice(-5));
      return;
    }

    const audioTracks = stream.getAudioTracks();
    if (!audioTracks || audioTracks.length === 0) {
      log.log('[SPEAKER DETECTOR] No audio tracks found for', connectionId, Date.now().toString().slice(-5));
      return;
    }

    // Remove any existing analyser for this connection before adding a new one,
    // to prevent audio graph node leaks on repeated ontrack events.
    this.removeStream(connectionId);

    try {
      const analyser = this.audioContext.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.5;

      const source = this.audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      this.audioAnalysers.set(connectionId, analyser);

      audioTracks[0].addEventListener('ended', () => this.removeStream(connectionId));

      log.log('[SPEAKER DETECTOR] Added stream for', connectionId, Date.now().toString().slice(-5));
    } catch (error) {
      log.log('[SPEAKER DETECTOR] Error adding stream:', error, Date.now().toString().slice(-5));
    }
  }

  public removeStream(connectionId: string): void {
    const analyser = this.audioAnalysers.get(connectionId);

    if (analyser) {
      analyser.disconnect();
    }

    this.audioAnalysers.delete(connectionId);
    this.lastSpeakTime.delete(connectionId);

    // If this was the active speaker, clear it
    if (this.currentActiveSpeaker === connectionId) {
      this.currentActiveSpeaker = null;
    }

    log.log('[SPEAKER DETECTOR] Removed stream for', connectionId, Date.now().toString().slice(-5));
  }

  private detectSpeaker(): void {
    const now = Date.now();
    let highestLevel = 0;
    let activeSpeakerId: string | null = null;

    // Process each connected stream individually
    this.audioAnalysers.forEach((analyser, connectionId) => {
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(dataArray);

      const level = this.calculateAudioLevel(dataArray);

      // Update last speak time if speaking
      if (level > this.options.threshold) {
        this.lastSpeakTime.set(connectionId, now);
      }

      // Check if this is the highest level
      if (level > highestLevel && level > this.options.threshold) {
        highestLevel = level;
        activeSpeakerId = connectionId;
      }
    });

    // If no active speaker found, use the most recent speaker (within timeout)
    if (!activeSpeakerId) {
      let mostRecentSpeakTime = 0;

      this.lastSpeakTime.forEach((lastSpeak, connectionId) => {
        // Only consider speakers within the timeout period
        if (now - lastSpeak < this.options.speakingTimeout && lastSpeak > mostRecentSpeakTime) {
          mostRecentSpeakTime = lastSpeak;
          activeSpeakerId = connectionId;
        }
      });
    }

    // If active speaker changed, emit event
    if (activeSpeakerId !== this.currentActiveSpeaker) {
      this.currentActiveSpeaker = activeSpeakerId;
      this.emit('activeSpeakerChanged', activeSpeakerId);
    }
  }

  private calculateAudioLevel(dataArray: Uint8Array): number {
    const start = Math.floor(dataArray.length * 0.2);
    const end = Math.floor(dataArray.length * 0.8);

    let sum = 0;
    for (let i = start; i < end; i += 4) {
      sum += dataArray[i];
    }

    return sum / ((end - start) / 4) / 255;
  }

  public getCurrentSpeaker(): string | null {
    return this.currentActiveSpeaker;
  }
}
